import { useMemo } from 'react';

export interface SummaryProviderUpdateInfo {
  provider_name: string;
  new_treatment_notes: string | null;
  new_treatment_status: string | null;
}

export interface CheckInSummary {
  check_in_time: string;
  general_notes: string | null;
  provider_updates: SummaryProviderUpdateInfo[];
}

export type ProviderDelta =
  | {
      type: 'NEW_PROVIDER';
      update: SummaryProviderUpdateInfo;
    }
  | {
      type: 'UPDATED_PROVIDER';
      update: SummaryProviderUpdateInfo;
      notesChanged: boolean;
      statusChanged: boolean;
      prevStatus: string | null;
    };

export interface CheckInDelta {
  checkIn: CheckInSummary;
  showGeneralNotes: boolean;
  providerDeltas: ProviderDelta[];
}

export const useCheckInDeltas = (checkIns: CheckInSummary[]): CheckInDelta[] =>
  useMemo(() => {
    const deltas: CheckInDelta[] = [];

    for (let i = 0; i < checkIns.length; i += 1) {
      const currentCheckIn = checkIns[i];
      const previousCheckIn = checkIns[i + 1] ?? null;

      const showGeneralNotes =
        !!currentCheckIn.general_notes &&
        currentCheckIn.general_notes !== previousCheckIn?.general_notes;

      const providerDeltas = currentCheckIn.provider_updates
        .map((currentUpdate) => {
          const prevUpdate = previousCheckIn?.provider_updates.find(
            (pu) => pu.provider_name === currentUpdate.provider_name,
          );

          if (!prevUpdate) {
            return {
              type: 'NEW_PROVIDER' as const,
              update: currentUpdate,
            };
          }

          const notesChanged = !!currentUpdate.new_treatment_notes;
          const statusChanged =
            currentUpdate.new_treatment_status !==
            prevUpdate.new_treatment_status;

          if (notesChanged || statusChanged) {
            return {
              type: 'UPDATED_PROVIDER' as const,
              update: currentUpdate,
              notesChanged,
              statusChanged,
              prevStatus: prevUpdate.new_treatment_status,
            };
          }

          return null;
        })
        .filter((delta): delta is NonNullable<typeof delta> => delta !== null);

      if (showGeneralNotes || providerDeltas.length > 0) {
        deltas.push({
          checkIn: currentCheckIn,
          showGeneralNotes,
          providerDeltas,
        });
      }
    }
    return deltas;
  }, [checkIns]);
